import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { api } from '../api';
import { Aviso, Etiqueta, Painel, type TomEtiqueta } from '../comum';

/**
 * Como o parque evoluiu.
 *
 * 🔴 **A seta nunca aparece sozinha.** Toda tendência vem com o número absoluto
 * (`+30 máquinas`), porque é ele que muda a decisão de quem lê — "+4,4%" exige
 * conta de cabeça sobre uma base que a pessoa não tem à mão.
 *
 * 🔴 **E `UNKNOWN` não vira seta.** Uma instalação de ontem tem um ponto na
 * série; desenhar "estável" nesse caso afirmaria estabilidade sobre dados que
 * ninguém coletou. Quando não há histórico, a tela diz que não há.
 */

export interface Tendencia {
  readonly metrica: string;
  readonly rotulo: string;
  readonly periodo: string;
  readonly de: number | null;
  readonly para: number | null;
  readonly delta: number | null;
  readonly pontosMedidos: number;
  readonly piorou: boolean | null;
  readonly tendencia: {
    readonly direction: 'UP' | 'DOWN' | 'STABLE' | 'UNKNOWN';
    readonly magnitude: number | null;
  };
}

interface Resposta {
  readonly janela: string;
  readonly periodo: string;
  readonly diasComBriefing: number;
  readonly aviso: string | null;
  readonly metricas: readonly Tendencia[];
  readonly piores: readonly Tendencia[];
  readonly melhores: readonly Tendencia[];
}

const JANELAS = [
  { id: 'ontem', rotulo: 'Ontem vs hoje' },
  { id: 'semana', rotulo: '7 dias' },
  { id: 'mes', rotulo: '30 dias' },
] as const;

/**
 * O símbolo e o tom saem de `piorou`, não da direção.
 *
 * `UP` no número de dispositivos é o parque crescendo — boa notícia. Ligar a
 * cor à direção pintaria isso de vermelho.
 */
export function aparenciaDa(t: Tendencia): {
  seta: string;
  tom: TomEtiqueta;
  texto: string;
} {
  if (t.tendencia.direction === 'UNKNOWN') {
    return { seta: '–', tom: 'neutro', texto: 'sem histórico suficiente' };
  }
  if (t.tendencia.direction === 'STABLE') {
    return { seta: '=', tom: 'neutro', texto: 'estável' };
  }
  const sinal = (t.delta ?? 0) > 0 ? '+' : '';
  return {
    seta: t.tendencia.direction === 'UP' ? '▲' : '▼',
    tom: t.piorou === true ? 'erro' : 'ok',
    // O absoluto primeiro, a porcentagem entre parênteses.
    texto:
      `${sinal}${t.delta}` +
      (t.tendencia.magnitude === null
        ? ''
        : ` (${sinal}${Math.round(t.tendencia.magnitude * 100)}%)`),
  };
}

function Linha({ t }: { t: Tendencia }): ReactNode {
  const a = aparenciaDa(t);
  return (
    <div className="linha" style={{ padding: '6px 0' }}>
      <span>
        {t.rotulo}{' '}
        {t.de !== null && t.para !== null && (
          <span className="suave">
            · {t.de} → {t.para}
          </span>
        )}
      </span>
      <Etiqueta tom={a.tom} titulo={`${t.periodo} · ${t.pontosMedidos} dia(s) medido(s)`}>
        {a.seta} {a.texto}
      </Etiqueta>
    </div>
  );
}

export function Tendencias({ aoPerderSessao }: { aoPerderSessao: () => void }): ReactNode {
  const [janela, setJanela] = useState<string>('semana');
  const [dados, setDados] = useState<Resposta | null>(null);

  const carregar = useCallback(async (): Promise<void> => {
    try {
      setDados(await api.get<Resposta>(`/api/tendencias?janela=${janela}`));
    } catch (e: unknown) {
      if ((e as { precisaLogin?: boolean }).precisaLogin === true) aoPerderSessao();
    }
  }, [aoPerderSessao, janela]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  return (
    <Painel titulo="Tendências" sub="Como o parque evoluiu, sobre o histórico de briefings.">
      <div className="linha" style={{ marginBottom: 12 }}>
        {JANELAS.map((j) => (
          <button
            key={j.id}
            className={`encolhe${janela === j.id ? ' primario' : ''}`}
            onClick={() => setJanela(j.id)}
          >
            {j.rotulo}
          </button>
        ))}
      </div>

      {dados?.aviso != null && (
        <Aviso tipo="info">
          {dados.aviso} O histórico se forma com o briefing diário — ele precisa ter rodado
          em pelo menos dois dias.
        </Aviso>
      )}

      {dados !== null && dados.aviso === null && (
        <>
          {dados.piores.length > 0 && (
            <>
              <h4>Piorou</h4>
              {dados.piores.map((t) => (
                <Linha key={t.metrica} t={t} />
              ))}
            </>
          )}
          {dados.melhores.length > 0 && (
            <>
              <h4>Melhorou</h4>
              {dados.melhores.map((t) => (
                <Linha key={t.metrica} t={t} />
              ))}
            </>
          )}
          {dados.piores.length === 0 && dados.melhores.length === 0 && (
            <p className="suave">Nada mudou de forma significativa em {dados.periodo}.</p>
          )}

          <details style={{ marginTop: 14 }}>
            <summary className="suave">
              Todas as métricas ({dados.diasComBriefing} dia(s) com briefing)
            </summary>
            {dados.metricas.map((t) => (
              <Linha key={t.metrica} t={t} />
            ))}
          </details>
        </>
      )}
    </Painel>
  );
}
